// ─── Hilo de pendientes al volver ────────────────────────────────────────────
// Al entrar (la sesión anterior se cerró por logout, inactividad, cambio de
// contraseña forzado o simplemente cerrando la pestaña), se toma una foto de lo
// que quedó a medias y se muestra un pop-up con el resumen y el vínculo a cada
// cosa. A esa lista se van sumando los borradores que se guarden DURANTE la
// sesión: guardar un documento en borrador es, literalmente, dejarlo a medias, y
// el hilo se pierde igual si la sesión se corta media hora después.
//
// Registrar no es interrumpir: al guardar un borrador NO se abre el pop-up ni
// salta el recordatorio, porque el usuario acaba de decidir seguir trabajando.
// El documento entra a la lista y aparece cuando la lista se muestre.
//
// Los pendientes son de CADA QUIEN: solo se muestran los borradores que dejó a
// medias el usuario que tiene la sesión abierta. Un borrador ajeno no es un
// hilo perdido propio —es ruido— y además deja ver trabajo que no le
// corresponde. Por eso cada documento guarda su autor (core/autoria.js), y el
// aviso lo dice con nombre propio aunque sea redundante: leer "lo dejaste tú"
// evita la duda de si el pendiente es de alguien más.
//
// Los pendientes son los documentos en borrador (CT, OP, OCP, RC, FACT). Hubo
// también un repaso de fichas de cliente sin contacto imprimible; se retiró al
// pasar esa pregunta a la propia cotización, donde es obligatoria al guardar:
// el dato ya queda garantizado ahí, y seguir insistiendo por lo que ahora es
// solo una preferencia del cliente era ruido, no hilo perdido.
//
// El flujo es de a UNO: "Continuar" abre lo que toca y cierra el pop-up; cuando
// eso se resuelve, el pop-up reaparece solo con lo que falte. Así nadie repite
// una CT, OP o RS del mismo tema por haber perdido el hilo.
//
// Si el usuario aplaza ("Más tarde"), no desaparece el asunto: queda abajo a la
// derecha un recordatorio que insiste hasta que se termine o se desista de forma
// explícita. Aplazar no puede ser una manera de olvidar.
//
// Todo esto vive fuera de #root (render() lo barrería) y por debajo de los
// avisos de seguridad.
import { esMio, nombreAutor } from "../core/autoria.js";
import { C, DOC_INFO, ESTADOS } from "../core/config.js";
import { state } from "../core/state.js";
import { cloudState } from "../core/supabaseClient.js";
import { btnStyle, escapeHTML, fmtDate } from "../core/utils.js";
import { PRIORIDAD, anclar, anclarConEntrada, desanclar, hacerArrastrable } from "./dock.js";
import { registerActions } from "./actions.js";
import { showDocForm } from "../modules/documentos.js";
import { showVerFactura } from "../modules/facturas.js";
import { showRevalidacionModal } from "../modules/configuracion.js";

// NC no tiene estado borrador: nace emitida.
const TIPOS_CON_BORRADOR = ["CT", "OP", "OCP", "RC", "FACT"];

let foto = [];          // [{tipo, id}] — borradores existentes al entrar
let activo = null;      // el pendiente que se está trabajando
let vigilante = null;   // intervalo que espera a que el activo se resuelva

function buscarDoc(tipo, id) {
  return (state.data[DOC_INFO[tipo].key] || []).find(d => d.id === id) || null;
}

function nombreTercero(tipo, doc) {
  const lista = tipo === "OCP" ? state.data.proveedores : state.data.clientes;
  const id = tipo === "OCP" ? doc.proveedor_id : doc.cliente_id;
  const t = (lista || []).find(x => x.id === id);
  return t ? t.nombre : "";
}

// ─── Desistir: la única salida que no es terminar ───────────────────────────
// Se guarda por inquilino y sobrevive a la recarga: desistir es una decisión,
// no un "no me molestes hasta que refresque".
function claveDescartados() {
  return "erp:pendientes_descartados:" + (cloudState.tenantId || "local");
}

function descartados() {
  try { return JSON.parse(localStorage.getItem(claveDescartados()) || "[]"); }
  catch { return []; }
}

function descartar(clave) {
  try {
    const lista = descartados();
    if (!lista.includes(clave)) lista.push(clave);
    localStorage.setItem(claveDescartados(), JSON.stringify(lista));
  } catch {}
}

function estaDescartado(clave) {
  return descartados().includes(clave);
}

// ─── Qué sigue pendiente ─────────────────────────────────────────────────────
// Lista unificada de pendientes vigentes, en el orden en que se muestran.
function tareasVigentes() {
  const tareas = [];
  for (const p of foto) {
    const d = buscarDoc(p.tipo, p.id);
    if (!d || d.estado !== "borrador" || !esMio(d)) continue;
    if (estaDescartado("doc:" + p.tipo + ":" + p.id)) continue;
    tareas.push({ clave: "doc:" + p.tipo + ":" + p.id, tipo: p.tipo, id: p.id, doc: d });
  }
  return tareas;
}

// Suma a la lista un documento que acaba de quedar en borrador. Se llama al
// guardar; si ya estaba, no se duplica.
export function registrarPendiente(tipo, id) {
  if (!TIPOS_CON_BORRADOR.includes(tipo) || !id) return;
  if (foto.some(p => p.tipo === tipo && p.id === id)) return;
  foto.push({ tipo, id });
  // Si el recordatorio de la esquina ya estaba a la vista, se refresca para que
  // el conteo no se quede corto. Si no estaba, no se invoca: sería interrumpir.
  if (document.getElementById("pendientes-insistente")) mostrarInsistente();
}

export function prepararPendientesAlEntrar() {
  foto = [];
  activo = null;
  for (const tipo of TIPOS_CON_BORRADOR) {
    for (const d of (state.data[DOC_INFO[tipo].key] || [])) {
      if (d.estado === "borrador" && esMio(d)) foto.push({ tipo, id: d.id });
    }
  }
  // La bienvenida aún se está desvaneciendo: el pop-up espera a que termine.
  if (tareasVigentes().length) setTimeout(() => mostrarPopup(false), 900);
}

// ─── Estilos del recordatorio insistente ─────────────────────────────────────
function inyectarEstilos() {
  if (document.getElementById("estilos-pendientes")) return;
  const st = document.createElement("style");
  st.id = "estilos-pendientes";
  st.textContent = `
    /* Latido permanente y suave: mantiene la tarjeta en el rabillo del ojo sin
       tapar nada ni robar el foco del teclado. */
    @keyframes pend-latido{
      0%,100%{box-shadow:0 10px 30px rgba(15,23,42,.18),0 0 0 0 rgba(245,158,11,.42)}
      50%    {box-shadow:0 10px 30px rgba(15,23,42,.18),0 0 0 9px rgba(245,158,11,0)}
    }
    /* Cada tanto se sacude: es lo que convierte "lo veo" en "lo atiendo".
       La sacudida parte SIEMPRE de --dock-dx/--dock-dy (dónde dejó el usuario
       la tarjeta al arrastrarla). Sin eso, esta animación pisaría el transform
       del arrastre y la tarjeta no se podría mover de su esquina. */
    @keyframes pend-sacudida{
      0%,88%,100%{transform:translate(var(--dock-dx,0px),var(--dock-dy,0px))}
      90%{transform:translate(calc(var(--dock-dx,0px) - 6px),var(--dock-dy,0px))}
      92%{transform:translate(calc(var(--dock-dx,0px) + 6px),var(--dock-dy,0px))}
      94%{transform:translate(calc(var(--dock-dx,0px) - 4px),var(--dock-dy,0px))}
      96%{transform:translate(calc(var(--dock-dx,0px) + 4px),var(--dock-dy,0px))}
    }
    #pendientes-insistente{
      animation:pend-latido 2.2s ease-in-out infinite, pend-sacudida 11s ease-in-out infinite;
    }
    #pendientes-insistente .barra i{display:block;height:100%;background:${C.amber};border-radius:99px;}
    @media (prefers-reduced-motion: reduce){
      #pendientes-insistente{animation:none;border-color:${C.amber};}
    }
  `;
  document.head.appendChild(st);
}

// ─── Pop-up principal ────────────────────────────────────────────────────────
function mostrarPopup(trasCompletar) {
  if (document.getElementById("popup-pendientes")) return;
  cerrarInsistente();
  const tareas = tareasVigentes();
  if (!tareas.length) { if (trasCompletar) mostrarTodoAlDia(); return; }

  const filas = tareas.map(t => {
    const info = DOC_INFO[t.tipo];
    const tercero = nombreTercero(t.tipo, t.doc);
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:9px 0;border-top:1px solid ${C.border};">
        <div style="font-size:18px;">${info.icon}</div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:12px;font-weight:700;color:${info.color};">${escapeHTML(t.doc.numero || info.prefix)} <span style="color:${C.muted};font-weight:600;font-size:10.5px;">· ${info.label}</span></div>
          <div style="font-size:10.5px;color:${C.muted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ${tercero ? escapeHTML(tercero) + " · " : ""}${t.doc.fecha ? fmtDate(t.doc.fecha) : ""} · ${ESTADOS[t.tipo].borrador}
          </div>
          ${nombreAutor(t.doc) ? `<div style="font-size:10px;color:${C.muted};margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
            ✋ lo dejaste tú, ${escapeHTML(nombreAutor(t.doc))}
          </div>` : ""}
        </div>
        <button data-action="pendiente-abrir" data-tipo="${t.tipo}" data-id="${t.id}"
          style="${btnStyle(C.mathiz, null, true)}white-space:nowrap;">Continuar →</button>
      </div>`;
  }).join("");

  const caja = document.createElement("div");
  caja.id = "popup-pendientes";
  caja.style.cssText = `
    position:fixed;top:0;right:0;bottom:0;left:0;z-index:930;display:flex;
    align-items:center;justify-content:center;background:rgba(15,23,42,.45);padding:16px;
  `;
  caja.innerHTML = `
    <div style="background:white;border-radius:14px;width:480px;max-width:100%;max-height:82vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);font-family:inherit;">
      <div style="height:4px;background:linear-gradient(90deg,${C.gold},${C.mathiz});border-radius:14px 14px 0 0;"></div>
      <div style="padding:15px 17px 14px;">
        <div style="font-size:14px;font-weight:800;color:${C.text};">📌 Retoma el hilo${state.usuarioNombre ? ", " + escapeHTML(state.usuarioNombre.split(" ")[0]) : ""}</div>
        ${trasCompletar
          ? `<div style="font-size:11px;color:#059669;font-weight:700;margin-top:4px;">✔ Uno menos. Esto sigue pendiente:</div>`
          : `<div style="font-size:11px;color:${C.muted};margin-top:4px;line-height:1.5;">
              Esto lo dejaste <b>a medias</b> antes de cerrar la sesión — es tuyo, nadie más lo ve.
              Termina una cosa a la vez:
              al completarla, esta lista vuelve a aparecer con lo que falte — así no creas otra
              cotización, orden o remisión del mismo tema sin darte cuenta.
            </div>`}
        <div style="margin-top:8px;">${filas}</div>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:12px;padding-top:10px;border-top:1px solid ${C.border};">
          <span style="font-size:10px;color:${C.muted};">Si aplazas, queda un recordatorio abajo a la derecha.</span>
          <button data-action="pendientes-cerrar" style="${btnStyle(null, "secondary")}">Más tarde</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(caja);
}

function cerrarPopup() {
  const caja = document.getElementById("popup-pendientes");
  if (caja) caja.remove();
}

function mostrarTodoAlDia() {
  cerrarInsistente();
  const nota = document.createElement("div");
  nota.style.cssText = `
    background:white;border:1px solid ${C.border};
    border-left:4px solid #059669;border-radius:10px;box-shadow:0 12px 34px rgba(15,23,42,.18);
    padding:11px 14px;font-family:inherit;font-size:11.5px;color:${C.text};font-weight:700;
  `;
  nota.textContent = "✅ Retomaste todos los pendientes de la sesión anterior.";
  anclarConEntrada(nota, PRIORIDAD.confirmacion);
  setTimeout(() => desanclar(nota), 5000);
}

// ─── Recordatorio insistente (al aplazar) ────────────────────────────────────
// Pequeño, abajo a la derecha, sin ✕: se sale de él terminando o desistiendo.
// No bloquea el trabajo — el usuario puede seguir usando todo el ERP.
function mostrarInsistente() {
  cerrarInsistente();
  const tareas = tareasVigentes();
  if (!tareas.length) return;
  inyectarEstilos();

  const t = tareas[0];
  const titulo = `${t.doc.numero || DOC_INFO[t.tipo].prefix} · ${DOC_INFO[t.tipo].label} en borrador`;
  const restantes = tareas.length - 1;

  const caja = document.createElement("div");
  caja.id = "pendientes-insistente";
  caja.style.cssText = `
    width:282px;max-width:calc(100vw - 32px);
    background:#fffbeb;border:2px solid ${C.amber};border-radius:12px;padding:12px 13px;
    font-family:inherit;color:${C.text};
  `;
  caja.innerHTML = `
    <div style="display:flex;align-items:center;gap:7px;">
      <span style="font-size:15px;">⏳</span>
      <div style="font-size:11.5px;font-weight:800;color:#92400e;flex:1;">Lo dejaste a medias${state.usuarioNombre ? ", " + escapeHTML(state.usuarioNombre.split(" ")[0]) : ""}</div>
      <span title="Arrastra para mover este aviso" style="font-size:13px;color:#d3a24a;letter-spacing:1px;line-height:1;">⠿</span>
    </div>
    <div style="font-size:11px;font-weight:700;margin-top:6px;line-height:1.4;">${escapeHTML(titulo)}</div>
    ${restantes > 0 ? `<div style="font-size:10px;color:#a16207;margin-top:2px;">y ${restantes} ${restantes === 1 ? "cosa más" : "cosas más"}</div>` : ""}
    <div style="font-size:10px;color:#78350f;margin-top:7px;line-height:1.5;">
      Esto no se va solo: termínalo o desiste de forma explícita.
    </div>
    <div style="display:flex;gap:7px;margin-top:10px;">
      <button data-action="pendientes-retomar" style="${btnStyle(C.amber, null, true)}flex:1;">Retomar</button>
      <button data-action="pendientes-desistir" style="${btnStyle(null, "secondary", true)}flex:1;">Desistir</button>
    </div>
  `;
  anclar(caja, PRIORIDAD.pendientes);
  // Se puede correr con el mouse (sin salirse de la ventana): el aviso es
  // insistente a propósito, pero no debe estorbar sobre lo que se esté leyendo.
  hacerArrastrable(caja);
}

function cerrarInsistente() {
  desanclar(document.getElementById("pendientes-insistente"));
}

// ─── Mantener los avisos al día ──────────────────────────────────────────────
// Los avisos se dibujaban una sola vez, y nada los revisaba después: al
// completar un documento seguían nombrándolo como pendiente hasta recargar la
// página. Esto los repasa tras cada cambio de la aplicación —se llama desde
// render()— y solo los vuelve a dibujar cuando la lista cambió de verdad, para
// que no parpadeen ni pierdan la posición donde el usuario los dejó.
let firmaMostrada = null;

export function sincronizarPendientes() {
  const insistente = document.getElementById("pendientes-insistente");
  const popup = document.getElementById("popup-pendientes");
  if (!insistente && !popup) { firmaMostrada = null; return; }

  const tareas = tareasVigentes();
  const firma = tareas.map(t => t.clave + ":" + (t.doc.numero || "")).join("|");
  if (firma === firmaMostrada) return;
  firmaMostrada = firma;

  if (!tareas.length) {
    // No queda nada a medias: los avisos se van solos.
    cerrarInsistente();
    cerrarPopup();
    return;
  }
  if (popup) { cerrarPopup(); mostrarPopup(false); }
  else if (insistente) mostrarInsistente();
}

// Confirmación de desistir: obliga a nombrar lo que se abandona, para que sea
// una decisión y no un clic de reflejo. Los borradores NO se borran.
function mostrarDesistir() {
  cerrarInsistente();
  const tareas = tareasVigentes();
  if (!tareas.length) return;
  const caja = document.createElement("div");
  caja.id = "popup-pendientes";
  caja.style.cssText = `
    position:fixed;top:0;right:0;bottom:0;left:0;z-index:931;display:flex;
    align-items:center;justify-content:center;background:rgba(15,23,42,.5);padding:16px;
  `;
  caja.innerHTML = `
    <div style="background:white;border-radius:14px;width:430px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.28);font-family:inherit;">
      <div style="padding:15px 17px 6px;">
        <div style="font-size:14px;font-weight:800;color:${C.text};">¿Desistir de los pendientes?</div>
        <div style="font-size:11px;color:${C.muted};margin-top:6px;line-height:1.55;">
          Dejarán de aparecer en el repaso y el recordatorio se irá.
          <b>Nada se borra</b>: los borradores siguen en su módulo — solo dejas de
          ser avisado.
        </div>
        <div style="margin-top:10px;background:#f8fafc;border-radius:8px;padding:9px 11px;font-size:11px;color:${C.text};">
          ${tareas.map(t => `<div style="padding:2px 0;">• ${escapeHTML(
            (t.doc.numero || DOC_INFO[t.tipo].prefix) + " — " + DOC_INFO[t.tipo].label
          )}</div>`).join("")}
        </div>
      </div>
      <div style="display:flex;gap:10px;justify-content:flex-end;padding:12px 17px 15px;">
        <button data-action="pendientes-desistir-cancelar" style="${btnStyle(null, "secondary")}">Seguir pendiente</button>
        <button data-action="pendientes-desistir-confirmar" style="${btnStyle(C.red)}">Sí, desisto</button>
      </div>
    </div>
  `;
  document.body.appendChild(caja);
}

// Espera a que el pendiente activo se resuelva y entonces sigue con lo que
// falte. Sondeo local barato (memoria, cada 1.5s) SOLO mientras hay activo.
function vigilarActivo() {
  clearInterval(vigilante);
  vigilante = setInterval(() => {
    if (!activo) { clearInterval(vigilante); return; }

    const d = buscarDoc(activo.tipo, activo.id);
    if (d && d.estado === "borrador") return;   // sigue a medias
    foto = foto.filter(p => !(p.tipo === activo.tipo && p.id === activo.id));
    activo = null;
    clearInterval(vigilante);
    // Sin modales abiertos encima: se reabre cuando el usuario cierre el actual.
    const reabrir = () => {
      if (state.modal || state.editingDoc) { setTimeout(reabrir, 1200); return; }
      mostrarPopup(true);
    };
    setTimeout(reabrir, 700);
  }, 1500);
}

registerActions({
  "pendiente-abrir": (d) => {
    const doc = buscarDoc(d.tipo, d.id);
    if (!doc) { cerrarPopup(); return; }
    activo = { tipo: d.tipo, id: d.id };
    cerrarPopup();
    cerrarInsistente();
    state.globalSearch = "";
    state.page = d.tipo;
    // Mismo destino que "ir-a-documento": la factura se abre en su visor,
    // el resto en su formulario (o en revalidación si está bloqueado).
    if (d.tipo === "FACT") { state.modal = null; showVerFactura(d.id); }
    else if (doc._locked) showRevalidacionModal(d.tipo, d.id);
    else showDocForm(d.tipo, doc);
    vigilarActivo();
  },
  "pendientes-cerrar": () => { cerrarPopup(); mostrarInsistente(); },
  "pendientes-retomar": () => { cerrarInsistente(); mostrarPopup(false); },
  "pendientes-desistir": () => mostrarDesistir(),
  "pendientes-desistir-cancelar": () => { cerrarPopup(); mostrarInsistente(); },
  "pendientes-desistir-confirmar": () => {
    tareasVigentes().forEach(t => descartar(t.clave));
    cerrarPopup();
    cerrarInsistente();
  }
});
